<template>
  <div>
    <section
      v-if="cartLoading"
      id="bag"
      class="bag bag--loading is-loading"
    ></section>

    <section
      v-else-if="cartCount > 0"
      id="bag"
      class="bag"
      :data-count="cartCount"
      :data-total-price="cartTotalPrice"
    >
      <Teleport to="#bagBtnCount">
        <span class="bag__count">
          {{ cartCount }}
        </span>
      </Teleport>
      <div class="bag__form container">
        <h2 class="bag__title">
          {{ title }} ({{ cartCount }})
        </h2>
        <div class="bag__content">
          <section class="bag__summary">
            <div class="bag__summary-inner">
              <div id="bagTotals" class="bag__totals">
                <div class="bag__summary-header">
                  <h2 class="bag__summary-title bag__title--summary">Order Summary</h2>
                </div>
                <CartThermometer
                :requiresNoShipping="requiresNoShipping"
                 @update:isAboveThreshold="handleThresholdUpdate"
                v-if="enableCartThermometer && !requiresShippingSurcharge" />
                <div class="bag__summary-totals">
                  <p class="bag__total">
                    <span class="bag__total-label">Subtotal</span>
                    <span id="bagSubtotal" class="bag__total-value">
                      {{ formattedItemsSubtotalPrice }}
                    </span>
                  </p>
                  <p v-if="requiresShippingSurcharge" class="bag__total">
                    <span class="bag__total-label bag__total-label--info">
                      Large Shipping Fee ({{ shippingSurchargeFrameCount }} {{ shippingSurchargeFrameCount === 1 ? 'item' : 'items' }})
                      <div class="tooltip" :class="isShippingTooltipOpen ? 'tooltip--active' : ''">
                        <button
                          type="button"
                          class="bag__info-icon tooltip__button"
                          aria-label="Large shipping fee information"
                          @click="toggleShippingTooltip(true)"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                            <path d="M8 7V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
                          </svg>
                        </button>
                        <div v-if="isShippingTooltipOpen" class="tooltip__modal tooltip__modal--text">
                          <p class="tooltip__text">
                            Our larger frames (sizes XL and G) require a Large Shipping Fee of $25 each. You can always pick up at a store for free.
                          </p>
                        </div>
                        <div
                          v-if="isShippingTooltipOpen"
                          class="tooltip-overlay"
                          @click="toggleShippingTooltip(false)"
                        />
                      </div>
                    </span>
                    <span class="bag__total-value">
                      ${{ 25 * shippingSurchargeFrameCount }}
                    </span>
                  </p>
                  <p
                  v-else-if="isAboveThreshold"
                  class="bag__total trk-shipping-section">
                    <span
                      class="bag__total-label"
                      v-html="cartShippingLineLabel"
                    >
                    </span>
                    <span class="bag__total-value trk-shipping-value">
                      {{ cartShippingLineValue }}
                    </span>
                  </p>
                  <p
                  v-else-if="requiresNoShipping"
                  class="bag__total trk-shipping-section">
                    <span
                      class="bag__total-label"
                      v-html="cartShippingLineLabel"
                    >
                    </span>
                    <span class="bag__total-value trk-shipping-value">
                      {{ cartShippingLineValue }}
                    </span>
                  </p>

                  <p
                  v-else
                  class="bag__total trk-shipping-section">
                    <span
                      class="bag__total-label"
                      v-html="belowCartShippingLineLabel"
                    >
                    </span>
                    <span class="bag__total-value trk-shipping-value">
                      {{ belowCartShippingLineValue }}
                    </span>
                  </p>
                  <p v-if="shippingFeeProduct" class="bag__total">
                    <span class="bag__total-label">
                      {{ cartSummaryMailInServiceCopy }}
                    </span>
                    <span class="bag__total-value">
                      {{ formattedMailInServicePrice }}
                    </span>
                  </p>
                  <p class="bag__total">
                    <span class="bag__total-label">
                      {{ cartSummaryTaxLineText }}
                    </span>
                    <span class="bag__total-value">
                      {{ cartSummaryTaxLineValue }}
                    </span>
                  </p>
                  <div class="bag__final-total">
                    <h3 class="bag__total bag__total--cart-total">
                      <span class="bag__total-label">Total</span>
                      <span
                      v-if ="isAboveThreshold"
                      id="bagTotal" class="bag__total-value">
                        {{ formattedItemsTotalPrice }}
                      </span>
                      <span v-else id="bagTotal" class="bag__total-value">
                        {{ totalWithShippingCharge }}
                      </span>
                    </h3>
                  </div>
                </div>
              </div>
            </div>
            <div class="bag__actions">
              <a
                v-if="checkoutAvailable"
                :href="checkoutUrl"
                :class="(!checkoutAvailable || checkoutDisabled || checkoutLoading && 'bag__checkout--disabled')"
                class="bag__action bag__checkout js-bag-checkout btn btn--secondary btn--full"
                name="checkout"
                title="Proceed to Checkout"
                @click="handleCheckoutClick"
              >
                <span v-if="checkoutDisabled || checkoutLoading" class="is-loading"></span>
                <span v-else>
                  Checkout
                  <span v-if="formattedItemsTotalPrice" class="hide-desktop" >
                    &nbsp;– {{ isAboveThreshold ? formattedItemsTotalPrice : totalWithShippingCharge }}
                  </span>
                </span>
              </a>
               <button
                v-else
                type="button"
                class="bag__action bag__checkout js-bag-checkout btn btn--secondary btn--full"
                :class="(checkoutDisabled || checkoutLoading && 'bag__checkout--disabled')"
                title="Proceed to Checkout"
                @click="displayErrorMessaging"
              >
                <span v-if="checkoutDisabled || checkoutLoading" class="is-loading"></span>
                <span v-else>
                  Checkout
                  <span v-if="formattedItemsTotalPrice" class="hide-desktop" >
                    &nbsp;– {{ isAboveThreshold ? formattedItemsTotalPrice : totalWithShippingCharge }}
                  </span>
                </span>
              </button>
              <p
                v-if="showErrorMessage"
                class="bag__content-error"
              >
                {{ cartCheckoutPackageError }}
              </p>
            </div>
            <GiftNote />
            <div class="bag__cart-tout" v-if="cartPageToutText">
              <div class="bag__cart-tout-image">
                <ResponsiveImage
                  :image="cartPageToutIcon"
                  image-alt="Cart Page Tout"
                  modifier="square"
                />
              </div>
              <span class="bag__cart-tout-text">{{ cartPageToutText }}</span>
            </div>
          </section>

          <section
            id="bagItems"
            class="bag__items"
          >
            <CartItem
              v-for="item in cartItems"
              :key="item?.id"
              :item="item"
              :allItems="cartItems"
              :checkoutAvailable="checkoutAvailable"
              :hasPackageError="hasPackageError"
              :currency="currency"
              :cartCount="cartCount"
              :itemPrice="item?.final_line_price"
              :itemProperties="(item?.properties || {})"
              :selectedDropOffStore="(storeSelectorData || null)"
              :selectedShipToUsStore="(shipToUsData || null)"
              :dropOffStores="computedDropOffStores"
              :togglePackingError="togglePackingError"
              :bundleDetails="bundleDetails"
              :sharedZipcode="sharedZipcode"
              :selectedPickupStore="selectedPickupStore"
              @set-ship-to-us="handleSetShipToUs"
              @toggle-checkout-button="toggleCheckoutButton"
              @zipcode-updated="handleZipcodeUpdated"
              @pickup-store-updated="handlePickupStoreUpdated"
            />
          </section>
        </div>
        <section
          v-if="cartPromotion"
          v-html="cartPromotion"
          class="bag__promotion"
        >
        </section>
      </div>
    </section>

    <section
      v-else-if="error"
      class="error"
    >
      {{ error }}
    </section>

    <section
      v-else
      id="bag"
      class="bag is-empty"
      :data-count="0"
    >
      <div id="bagEmpty" class="container bag__empty">
        <h1 class="bag__empty-title">{{ emptyCartTitle }}</h1>
        <a
          v-if="hasEmptyCartCta1"
          class="btn btn--primary bag__empty-cta"
          :href="emptyCartCta1.url"
        >
          {{ emptyCartCta1.copy }}
        </a>
        <div v-if="hasEmptyCartCta2" class="bag__empty-cta-wrapper">
          <a
            class="btn btn--primary bag__empty-cta"
            :href="emptyCartCta2.url"
          >
            {{ emptyCartCta2.copy }}
          </a>
        </div>
      </div>
    </section>

    <section
      v-if="!wishlistLoading && wishlist && wishlist.length > 0"
      :class="cartCount == 0 ? 'bag bag--wishlist' : 'bag bag--wishlist-mobile'"
      class="wishlist-bag"
    >
      <div class="bag__form container">
        <hr class="hide-mobile" v-if="cartCount > 0" />
        <h2 class="bag__title bag__title--wishlist">{{ wishlistTitle }}</h2>
        <div class="bag__content">
          <section
            class="bag__items"
          >
            <CartItem
              v-for="item in wishlist"
              :key="item?.id"
              :item="item"
              :allItems="wishlist"
              :currency="currency"
              :itemPrice="item?.final_line_price"
              :itemProperties="(item?.properties || {})"
              :selectedDropOffStore="(storeSelectorData || null)"
              :dropOffStores="computedDropOffStores"
              :isWishlist="true"
            />
          </section>
        </div>
      </div>
    </section>

    <section
      v-else-if="wishlistError"
      class="error"
    >
      {{ wishlistError }}
    </section>
    <StoreSelector
      @set-drop-off-store="handleSetStoreData"
    ></StoreSelector>
  </div>
</template>

<script>
import {
  Teleport,
  computed,
  onBeforeMount,
  toRef,
  ref,
  watch,
  nextTick,
  watchEffect,
} from 'vue';
import CartItem from '../cart-item/CartItem';
import useCart from '../composables/cart-actions';
import useCartPromotion from '../composables/cart-promo';
import useDelivery from "../composables/delivery-estimator";
import ResponsiveImage from '../common/responsive-image/ResponsiveImage';
import StoreSelector from '../store-selector/StoreSelector';
import GiftNote from '../gift-note/GiftNote';
import CartThermometer from './CartThermometer';

export default {
  name: 'CartApp',
  components: {
    CartItem,
    Teleport,
    StoreSelector,
    ResponsiveImage,
    GiftNote,
    CartThermometer,
  },
  setup() {
    const {
      cartCount,
      cartItems,
      cartLoading,
      cartTotalPrice,
      checkoutUrl,
      itemsSubtotalPrice,
      currency,
      error,
      cartThemeSettings,
      fetchCart,
      wishlist,
      wishlistLoading,
      wishlistError,
      fetchWishlist,
      checkoutLoading,
      validateGiftCardDates,
    } = useCart();
    const { cartPromotion } = useCartPromotion();
    const { zipcode: deliveryZipcode } = useDelivery();
    const storage = window.sessionStorage;
    const widgetTag = SDG.Data.stockistWidgetTag;
    const storeSelectorData = toRef(null);
    const shipToUsData = toRef(null);
    const sharedZipcode = ref(deliveryZipcode.value || sessionStorage.getItem("SDG-zip") || null);
    // Shared pickup store for all cart items - reads from localStorage initially
    const savedStore = window.localStorage.getItem("selectedStore");
    const parsedStore = savedStore ? JSON.parse(savedStore) : null;
    const selectedPickupStore = ref(parsedStore || null);
    const checkoutAvailable = ref(true);
    const checkoutDisabled = ref(false);
    const computedDropOffStores = ref(null);
    const hasPackageError = ref(false);
    const showErrorMessage = ref(false);
    const title = SDG.Data.cartPageTitle || 'Your Cart';
    const enableCartThermometer = SDG.Data.cart.enable_cart_thermometer || false;
    const requiresNoShipping = ref(false);
    const isShippingTooltipOpen = ref(false);
    const tempShip = 'temp_ship';
    const tempDropOff = 'temp_drop_off';

    const {
      belowCartShippingLineLabel,
      belowCartShippingLineValue,
      belowThresholdShippingCharge,
      cartShippingLineLabel,
      cartShippingLineValue,
      cartSummaryMailInServiceCopy,
      cartSummaryMailInServiceProductVariantId,
      cartSummaryTaxLineText,
      cartSummaryTaxLineValue,
      cartPageToutIcon,
      cartPageToutText,
      cartCheckoutPackageError,
    } = cartThemeSettings;

    // Bundle handling for gallery walls
    const baseUrl = SDG.Data.joineryHost || 'https://staging.framebridge.com';
    const bundleDetails = ref({});

    const bundleItems = computed(() => {
      return cartItems?.value?.filter((item) =>
        item?.product_type === 'Frame Bundle' && item?.properties?._bundle_spec_number
      ) || [];
    });

    const bundleNumbersKey = computed(() => {
      return bundleItems.value
        .map(b => b.properties._bundle_spec_number)
        .sort()
        .join(',');
    });

    watchEffect(async () => {
      if (!bundleNumbersKey.value) {
        bundleDetails.value = {};
        return;
      }

      const bundleMap = {};
      bundleItems.value.forEach(b => {
        bundleMap[b.properties._bundle_spec_number] = b.properties._bundle_spec_authorization_token;
      });

      const details = {};
      for (const bundleNumber in bundleMap) {
        const authToken = bundleMap[bundleNumber];
        try {
          const response = await fetch(
            `${baseUrl}/api/v1/bundle_specifications/${bundleNumber}`,
            {
              headers: {
                'Framebridge-Resource-Authorization-Token': authToken
              }
            }
          );
          if (response.ok) {
            const data = await response.json();
            details[bundleNumber] = data.components
              .map(c => c.frameSpec?.number)
              .filter(Boolean);
          }
        } catch (error) {
          console.error(`Error fetching bundle ${bundleNumber}:`, error);
        }
      }
      bundleDetails.value = details;
    });

    const shippingFeeProduct = computed(() => {
      const shipFee = cartItems?.value?.find((item) => item?.properties?._is_shipping_fee === 'true' || item?.variant_id === cartSummaryMailInServiceProductVariantId);
      return shipFee;
    });

    const shippingSurchargeFrameCount = computed(() => {
      if (!cartItems?.value) return 0;

      const largeSizeGroups = ['Extra Large', 'Grand', 'Extra Grand', 'Mega'];

      return cartItems.value.filter((item) => {
        const artSize = item?.properties?.['Art Size'] || item?.properties?.['Photo Size'];
        if (!artSize) return false;

        // Parse the size group from the "Art Size" property (format: "Grand • 26" x 35"")
        const sizeGroupMatch = artSize.split('•')[0]?.trim();
        return largeSizeGroups.includes(sizeGroupMatch);
      }).length;
    });

    const requiresShippingSurcharge = computed(() => shippingSurchargeFrameCount.value > 0);

    const shippingSurchargeAmount = computed(() => 2500 * shippingSurchargeFrameCount.value);

    const formattedItemsSubtotalPrice = computed(() => {
      if (shippingFeeProduct?.value) {
        return __.formatPriceDisplay(itemsSubtotalPrice.value - shippingFeeProduct.value.price);
      } else {
        return __.formatPriceDisplay(itemsSubtotalPrice.value);
      }
    });

    const formattedItemsTotalPrice = computed(() => {
      let total = itemsSubtotalPrice.value;
      if (requiresShippingSurcharge.value) {
        total += shippingSurchargeAmount.value;
      }
      return __.formatPriceDisplay(total);
    });

    const formattedMailInServicePrice = computed(() => (
      __.formatPriceDisplay(shippingFeeProduct?.value?.price)
    ));

    const totalWithShippingCharge = computed(() => {
      let total = itemsSubtotalPrice.value;

      const shippingCharge = Number(belowThresholdShippingCharge.replace('.', ''));
      if (!Number.isNaN(shippingCharge)) {
        total += shippingCharge;
      }

      if (requiresShippingSurcharge.value) {
        total += shippingSurchargeAmount.value;
      }

      return __.formatPriceDisplay(total);
    });

    const isAboveThreshold = ref(true);
    const handleThresholdUpdate = (newValue) => {
      if (!enableCartThermometer) {
        isAboveThreshold.value = true;
      } else {
        isAboveThreshold.value = newValue;
      }
    };

    const toggleShippingTooltip = (shouldOpen) => {
      isShippingTooltipOpen.value = shouldOpen;
    };

    const formattedCartTotalPrice = computed(() => __.formatPriceDisplay(cartTotalPrice.value));

    const handleSetStoreData = (data) => {
      storeSelectorData.value = data;
      shipToUsData.value = null;
      // Update shared pickup store when a store is selected from the modal
      if (data?.storeName) {
        selectedPickupStore.value = data;
        window.localStorage.setItem("selectedStore", JSON.stringify(data));
      }
    };

    const handleSetShipToUs = (data) => {
      shipToUsData.value = data;
      storeSelectorData.value = null;
    };

    const handleZipcodeUpdated = (data) => {
      sharedZipcode.value = data.zipcode;
    };

    const handlePickupStoreUpdated = (storeData) => {
      selectedPickupStore.value = storeData;
      window.localStorage.setItem("selectedStore", JSON.stringify(storeData));
    };

    const toggleCheckoutButton = (val) => {
      checkoutDisabled.value = val;
    };

    const handleRequiresShipping = () => {
      requiresNoShipping.value = cartItems.value.every((item) => item.requires_shipping === false);
    };

    /**
     * Set the Stockist store data
     */
    const setDefaultDropOffStores = async () => {
      if (storage) storage.setItem('dropOffStores', null);
      const ipStackData = storage.getItem('ipStackData')
        ? JSON.parse(storage.getItem('ipStackData'))
        : null;

      const { latitude, longitude } = ipStackData || {};
      const stockistEndpoint = latitude && longitude
        ? `https://stockist.co/api/v1/${widgetTag}/locations/search?latitude=${latitude}&longitude=${longitude}`
        : `https://stockist.co/api/v1/${widgetTag}/locations/all`;

      const results = await fetch(stockistEndpoint)
        .then((searchResult) => searchResult.json())
        .catch((err) => console.error(err));

      const filteredResults = (latitude && longitude ? results.locations : results)
        .filter((result) => (
          result.custom_fields.find((field) => (
            field.name.toLowerCase() === 'drop off in store' && field.value.toLowerCase() === 'true'
          ))
        ))
        .filter((store) => {
          const badgeField = store.custom_fields?.find(
            (field) => field.name.includes('Badge')
          );
          return badgeField?.value !== 'Coming Soon!';
        });

      if (storage) storage.setItem('dropOffStores', JSON.stringify(filteredResults));
      computedDropOffStores.value = filteredResults;
    };

    const {
      title: emptyCartTitle,
      cta1: emptyCartCta1,
      cta2: emptyCartCta2,
    } = SDG?.Data?.emptyCart || {};

    const hasEmptyCartCta1 = computed(() => {
      if (!emptyCartCta1) return false;

      return emptyCartCta1?.copy !== '' && emptyCartCta1?.url !== '';
    });

    const hasEmptyCartCta2 = computed(() => {
      if (!emptyCartCta2) return false;

      return emptyCartCta2?.copy !== '' && emptyCartCta2?.url !== '';
    });

    const wishlistTitle = computed(() => {
      if (!wishlist) return '';
      const wishlistLength = wishlist.value.length;
      const pluralString = wishlistLength > 1 ? 's' : '';
      return `${wishlistLength} Item${pluralString} Saved For Later`;
    });

    const togglePackingError = (val) => {
      const itemsWithShipping = cartItems?.value?.filter((item) => item?.variant_options?.includes('Physical') && item?.product_type !== 'Gift Card') || null;

      if (val) {
        checkoutAvailable.value = false;
        showErrorMessage.value = true;
        hasPackageError.value = true;
      } else if (!val && itemsWithShipping?.length > 0) {
        let hasValidShipping = true;
        itemsWithShipping.forEach((item) => {
          const props = item?.properties || {};
          const { _dropOffAtStore: dropOff } = props;
          const packagingTypeKey = (typeof SDG !== 'undefined' && SDG?.Data?.cart?.packaging_type_property_label) || 'Packaging Type';
          const shipToUs = props[packagingTypeKey] || props.shipToUs;

          if ((!dropOff || dropOff === tempDropOff) && (!shipToUs || shipToUs === tempShip)) {
            hasValidShipping = false;
          }
        });

        checkoutAvailable.value = hasValidShipping;
        showErrorMessage.value = !hasValidShipping;
        hasPackageError.value = !hasValidShipping;
      } else {
        checkoutAvailable.value = true;
        showErrorMessage.value = false;
        hasPackageError.value = false;
      }
    };

    const handleCheckoutClick = async (event) => {
      if (!cartItems.value?.length) return;
      event.preventDefault();
      checkoutDisabled.value = true;
      try {
        await validateGiftCardDates();
      } finally {
        checkoutDisabled.value = false;
      }
      window.location.href = checkoutUrl.value;
    };

    const displayErrorMessaging = () => {
      hasPackageError.value = true;
      showErrorMessage.value = true;
      const itemsWithShipping = cartItems?.value?.filter((item) => item?.variant_options?.includes('Physical') && item?.product_type !== 'Gift Card') || null;

      if (itemsWithShipping?.length > 0) {
        itemsWithShipping.find((item) => {
          const props = item?.properties || {};
          const { _dropOffAtStore: dropOff } = props;
          const packagingTypeKey = (typeof SDG !== 'undefined' && SDG?.Data?.cart?.packaging_type_property_label) || 'Packaging Type';
          const shipToUs = props[packagingTypeKey] || props.shipToUs;
          const { variant_id: variantId, id } = item || {};

          if ((!dropOff || dropOff === tempDropOff) && (!shipToUs || shipToUs === tempShip)) {
            checkoutAvailable.value = false;

            if (variantId) {
              nextTick(() => {
                const itemEl = document.getElementById(`bagItem${variantId}-${id}`);

                if (itemEl) {
                  // slight timeout for cart to complete loading
                  setTimeout(() => {
                    itemEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 500);
                }
              });
            }
            return true;
          }
          return false;
        });
      }
    };

    onBeforeMount(() => {
      fetchCart();
      fetchWishlist();
      setDefaultDropOffStores();
    });

    // Watch zipcode for updates, handles if it's not immediately available on initial load
    watch(deliveryZipcode, (newZip) => {
      if (newZip && !sharedZipcode.value) {
        sharedZipcode.value = newZip;
      }
    });

    watch(
      [cartCount, wishlist, cartLoading, wishlistLoading],
      ([newCartCount, newWishlist, newCartLoading, newWishlistLoading]) => {
        nextTick(() => {
          if (newCartLoading || newWishlistLoading) return;
          if (newCartCount > 0 || newWishlist.length > 0) {
            SDG.Bag.empty(false);
          } else {
            SDG.Bag.empty(true);
          }
          // make sure the body is scrollable when the cart items change
          __.removeClass(document.body, 'overflow-hidden');
        });
      },
      { immediate: true },
    );

    // Handles Checkout cta disable/enable state
    watch(cartItems, (newCartItems) => {
      if (newCartItems?.length > 0) {
        const itemsWithShipping = newCartItems.filter((item) => item?.variant_options?.includes('Physical') && item?.product_type !== 'Gift Card');
        handleRequiresShipping();
        if (itemsWithShipping?.length > 0) {
          const uncheckedItem = itemsWithShipping.find((item) => {
            const props = item?.properties || {};
            const { _dropOffAtStore: dropOff } = props;
            const packagingTypeKey = (typeof SDG !== 'undefined' && SDG?.Data?.cart?.packaging_type_property_label) || 'Packaging Type';
            const shipToUs = props[packagingTypeKey] || props.shipToUs;

            if ((!dropOff || dropOff === tempDropOff)&& (!shipToUs || shipToUs === tempShip)) {
              checkoutAvailable.value = false;
              return true;
            }
            return false;
          });

          if (!uncheckedItem) {
            checkoutAvailable.value = true;
            hasPackageError.value = false;
            showErrorMessage.value = false;
          }
        } else {
          checkoutAvailable.value = true;
          hasPackageError.value = false;
          showErrorMessage.value = false;
        }
      }
    });

    return {
      cartCount,
      cartItems,
      cartPromotion,
      cartLoading,
      cartTotalPrice,
      checkoutUrl,
      currency,
      error,
      formattedCartTotalPrice,
      formattedItemsSubtotalPrice,
      formattedItemsTotalPrice,
      cartShippingLineLabel,
      cartShippingLineValue,
      cartSummaryMailInServiceCopy,
      cartSummaryMailInServiceProductVariantId,
      formattedMailInServicePrice,
      cartSummaryTaxLineText,
      cartCheckoutPackageError,
      cartSummaryTaxLineValue,
      cartPageToutIcon,
      cartPageToutText,
      checkoutAvailable,
      checkoutDisabled,
      checkoutLoading,
      emptyCartTitle,
      emptyCartCta1,
      emptyCartCta2,
      hasEmptyCartCta1,
      hasEmptyCartCta2,
      title,
      wishlist,
      wishlistLoading,
      wishlistError,
      wishlistTitle,
      handleSetStoreData,
      handleSetShipToUs,
      handleZipcodeUpdated,
      handlePickupStoreUpdated,
      sharedZipcode,
      selectedPickupStore,
      toggleCheckoutButton,
      computedDropOffStores,
      storeSelectorData,
      shipToUsData,
      shippingFeeProduct,
      requiresShippingSurcharge,
      shippingSurchargeFrameCount,
      hasPackageError,
      showErrorMessage,
      displayErrorMessaging,
      handleCheckoutClick,
      togglePackingError,
      enableCartThermometer,
      isAboveThreshold,
      handleThresholdUpdate,
      isShippingTooltipOpen,
      toggleShippingTooltip,
      belowCartShippingLineLabel,
      belowCartShippingLineValue,
      totalWithShippingCharge,
      requiresNoShipping,
      bundleDetails,
    };
  },
};
</script>
